import * as React from "react";
import EnhancedTable, {TableState} from "../../EnhancedTable/EnhancedTable";
import Modal from "../../Modal/Modal";
import PaperFormWrapper from "../../PaperFormWrapper/PaperFormWrapper";
import {Button, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, useTheme} from "@mui/material";
import NoteCard from "../../NoteCard/NoteCard";
import {useSnackbar} from "notistack";
import {
    useAllDeliveriesTableData, useCollectionStatusTableData,
    useCountryMap,
    useFavoriteAllDeliveriesTableData, useFavoriteCollectionStatusTableData,
    useRecentDeliveriesTableData,
    useRegionMap, useToggleCollectionStatusFavorite,
    useToggleDeliveryFavorite,
    useUpdateAllDeliveriesRow,
    useUpdateCollectionStatusRow,
    useUpdateRecentDeliveriesRow
} from "../../../hooks/refinitiv";

import {HeadCellConfig} from "../../EnhancedTable/EnhancedTableHeaderRow";
import {FileTextTwoTone} from "@ant-design/icons";
import LinkButton from "../../LinkButton/LinkButton";

interface RefinitivTableProps {
    mode: 'recentDeliveries' | 'allDeliveries' | 'collectionStatuses';
    favoritesOnly?: boolean;
}

export default (props: RefinitivTableProps) => {
    const favoritesOnly = props.favoritesOnly === true;

    const [tableState, setTableState] = React.useState(({
        page: 1,
        pageSize: (props.mode === 'recentDeliveries' && !props.favoritesOnly) ? -1 : 10,
        orderBy: props.mode === 'allDeliveries' ? 'date' : 'name',
        order: props.mode === 'allDeliveries' ? 'desc' : 'asc',
        searchString: null
    } as TableState));

    const [editIsOpen, setEditIsOpen] = React.useState(false);
    const [noteIsOpen, setNoteIsOpen] = React.useState(false);
    const [openNote, setOpenNote] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [rowToEdit, setRowToEdit] = React.useState(null);

    const theme = useTheme();
    const {enqueueSnackbar} = useSnackbar();
    const regionMap = useRegionMap();
    const countryMap = useCountryMap();

    // Data hooks are dependent on the view mode.
    let tableData;
    let mutator;
    let favoritesMutator;
    let allowAllRows = false;

    if (props.mode === 'allDeliveries') {
        tableData = favoritesOnly ? useFavoriteAllDeliveriesTableData(tableState) : useAllDeliveriesTableData(tableState);
        mutator = useUpdateAllDeliveriesRow();
        favoritesMutator = useToggleDeliveryFavorite()
    } else if (props.mode === 'recentDeliveries') {
        tableData = favoritesOnly ? useRecentDeliveriesTableData(tableState, true) : useRecentDeliveriesTableData(tableState, false);
        mutator = useUpdateRecentDeliveriesRow();
        favoritesMutator = useToggleDeliveryFavorite()
        allowAllRows = true;
    } else if (props.mode === 'collectionStatuses') {
        tableData = favoritesOnly ? useFavoriteCollectionStatusTableData(tableState) : useCollectionStatusTableData(tableState);
        mutator = useUpdateCollectionStatusRow();
        favoritesMutator = useToggleCollectionStatusFavorite();
    }

    let columns: HeadCellConfig[];
    if (props.mode === 'allDeliveries' || props.mode === 'recentDeliveries') {
        columns = [
            {
                key: "favorite",
                label: null,
                sortable: false
            },
            {
                key: "name",
                label: "Collection Name"
            },
            {
                key: "date",
                label: "Date (UTC)",
                formatter: (row) => {
                    const rowDate = new Date(Date.parse(row.date))
                    const dateString = rowDate.toLocaleDateString(
                        'en-us',
                        {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            timeZone: 'utc'
                        }
                    );
                    const timeString = rowDate.toLocaleTimeString('en-US', {
                        timeZone: 'utc',
                        timeStyle: 'short'
                    });
                    return `${dateString}, ${timeString} `
                }
            },
            {
                key: "status",
                label: "Status",
                formatter: (row) => {
                    if (row.status === 0) {
                        return "Successfully Collected";
                    }
                    return row.error_message
                        ? `Failed Collection: ${row.error_message}`
                        : "Failed Collection - Generic: DB provided no error message.";
                }
            },
            {
                key: "region",
                label: "Region",
                formatter: (row) => {
                    return row.metadata?.region ? row.metadata.region.name : null
                },
                sortable: false
            },
            {
                key: "country",
                label: "Country",
                formatter: (row) => {
                    return row.metadata?.country ? row.metadata.country.name : null
                },
                sortable: false
            },
            {
                key: "comment",
                label: "Note",
                formatter: (row) => {
                    return row.metadata?.comment ? (<IconButton
                        color="primary"
                        sx={{p: 0, ml: 1}}
                        size='small'
                        onClick={() => openNoteModal(row.metadata?.comment)}
                        title="Open Note"
                        aria-label="Open note">
                        <FileTextTwoTone twoToneColor={theme.palette.primary.main}/>
                    </IconButton>) : null
                },
                sortable: false
            },
            {
                key: "jira_link",
                label: "JIRA",
                formatter: (row) => {
                    return row.metadata?.jira_link ? (
                        <LinkButton title="View in JIRA" href={row.metadata.jira_link}/>) : null
                },
                sortable: false
            }
        ];
    } else {
        columns = [
            {
                key: "favorite",
                label: null,
                sortable: false
            },
            {
                key: "name",
                label: "Collection Name"
            },
            {
                key: "date",
                label: "Date (UTC)"
            },
            {
                key: "status",
                label: "Status",
                formatter: (row) => {
                    if (row.status === 0) {
                        return "Successfully Collected";
                    }
                    return row.error_message
                        ? `Failed Collection: ${row.error_message}`
                        : "Failed Collection - Generic: DB provided no error message.";
                }
            },
            {
                key: "region",
                label: "Region",
                formatter: (row) => {
                    return row.metadata?.region ? row.metadata.region.name : null
                },
                sortable: false
            },
            {
                key: "country",
                label: "Country",
                formatter: (row) => {
                    return row.metadata?.country ? row.metadata.country.name : null
                },
                sortable: false
            },
            {
                key: "comment",
                label: "Note",
                formatter: (row) => {
                    return row.metadata?.comment ? (<IconButton
                        color="primary"
                        sx={{p: 0, ml: 1}}
                        size='small'
                        onClick={() => openNoteModal(row.metadata?.comment)}
                        title="Open Note"
                        aria-label="Open note">
                        <FileTextTwoTone twoToneColor={theme.palette.primary.main}/>
                    </IconButton>) : null
                },
                sortable: false
            },
            {
                key: "jira_link",
                label: "JIRA",
                formatter: (row) => {
                    return row.metadata?.jira_link ? (
                        <LinkButton title="View in JIRA" href={row.metadata.jira_link}/>) : null
                },
                sortable: false
            }
        ];
    }

    let hasError = regionMap.isError || countryMap.isError || tableData.isError;
    let isLoading = (regionMap.isLoading || countryMap.isLoading || tableData.isLoading);
    let tableLoadingState: 'loading' | 'success' | 'error';
    if (hasError) {
        tableLoadingState = 'error';
    } else if (isLoading) {
        tableLoadingState = 'loading';
    } else {
        tableLoadingState = 'success';
    }

    const closeModals = () => {
        setEditIsOpen(false);
        setNoteIsOpen(false);
        setLoading(false);
    }

    const handleEdit = (row) => {
        setRowToEdit(row);
        setEditIsOpen(true)
    };

    const openNoteModal = (note) => {
        setOpenNote(note)
        setNoteIsOpen(true)
    }

    const handleTableStateChange = (newState: TableState) => {
        setTableState(newState);
    }

    const handleFieldChange = (e) => {
        setRowToEdit({
            ...rowToEdit,
            metadata: {
                ...rowToEdit.metadata,
                [e.target.name]: e.target.value ? e.target.value : null
            }
        });
    }

    const handleFavorite = async (row) => {
        favoritesMutator.mutate(
            {row: row, tableState: tableState},
            {
                onSuccess: () => {
                    const action = row.favorite ? "removed from" : "added to";
                    enqueueSnackbar(`Successfully ${action} favorites.`, {
                        preventDuplicate: true,
                        variant: 'success',
                        anchorOrigin: {horizontal: 'right', vertical: 'bottom'}
                    });
                    closeModals()
                },
                onError: (e) => {
                    console.error(e);
                    enqueueSnackbar('An error occurred, please try again.', {
                        preventDuplicate: true,
                        variant: 'error',
                        anchorOrigin: {horizontal: 'right', vertical: 'bottom'}
                    });
                    closeModals()
                }
            }
        );
    };

    const submitMetadataChange = async (e) => {
        e.preventDefault()
        setLoading(true);

        const {
            name,
            metadata
        } = rowToEdit;

        // If the id is missing it means we need to create a new metadata entry.
        const metadataId = metadata.id || null;
        if (!metadataId) {
            metadata.collection_id = name;
            metadata.table = "PostHistory";
        }

        mutator.mutate(
            {row: rowToEdit, tableState: tableState},
            {
                onSuccess: () => {
                    enqueueSnackbar('Row successfully updated.', {
                        preventDuplicate: true,
                        variant: 'success',
                        anchorOrigin: {horizontal: 'right', vertical: 'bottom'}
                    });
                    closeModals()
                },
                onError: (e) => {
                    console.error(e);
                    enqueueSnackbar('An error occurred, please try again.', {
                        preventDuplicate: true,
                        variant: 'error',
                        anchorOrigin: {horizontal: 'right', vertical: 'bottom'}
                    });
                    closeModals()
                }
            }
        )
    }

    return (
        <React.Fragment>
            <EnhancedTable
                loadingState={tableLoadingState}
                backgroundLoading={tableData.isFetching && !tableData.isLoading}
                columns={columns}
                rows={tableData?.data?.results}
                rowCount={tableData?.data?.count}
                tableState={tableState}
                canEdit={true}
                editHandler={handleEdit}
                stateHandler={handleTableStateChange}
                favoriteHandler={handleFavorite}
                allowAllRows={allowAllRows}
            />
            <Modal open={editIsOpen} onClose={closeModals}>
                <PaperFormWrapper
                    topPadding={0}
                    title="Edit Details"
                    onSubmit={submitMetadataChange}
                    loading={loading}
                >
                    <TextField
                        size="small"
                        margin="normal"
                        fullWidth
                        name='Collection Name'
                        label='Collection Name'
                        type="text"
                        disabled={true}
                        value={rowToEdit?.name}
                    />
                    <FormControl size="small" margin="normal" required fullWidth sx={{minWidth: '250px'}}>
                        <InputLabel id="region-label">Region</InputLabel>
                        <Select
                            fullWidth
                            labelId="region-label"
                            name="region_id"
                            value={rowToEdit?.metadata?.region_id || ''}
                            label="Region"
                            disabled={loading}
                            onChange={handleFieldChange}
                        >
                            <MenuItem value="">None</MenuItem>
                            {
                                tableLoadingState == 'success' && (
                                    Object.entries(regionMap.data).map(region => (
                                        <MenuItem key={region[0]} value={region[0]}>{(region[1] as string)}</MenuItem>
                                    ))
                                )
                            }
                        </Select>
                    </FormControl>
                    <FormControl size="small" margin="normal" required fullWidth sx={{minWidth: '250px'}}>
                        <InputLabel id="country-label">Country</InputLabel>
                        <Select
                            fullWidth
                            labelId="country-label"
                            name="country_id"
                            value={rowToEdit?.metadata?.country_id || ''}
                            label="Country"
                            disabled={loading}
                            onChange={handleFieldChange}
                        >
                            <MenuItem value="">None</MenuItem>
                            {
                                tableLoadingState == 'success' && (
                                    Object.entries(countryMap.data).map(country => (
                                        <MenuItem key={country[0]} value={country[0]}>{(country[1] as string)}</MenuItem>
                                    ))
                                )
                            }
                        </Select>
                    </FormControl>
                    <TextField
                        size="small" margin="normal" required fullWidth sx={{minWidth: '250px'}}
                        name="jira_link"
                        value={rowToEdit?.metadata?.jira_link || ''}
                        label="JIRA URL"
                        disabled={loading}
                        onChange={handleFieldChange}
                    >
                    </TextField>
                    <TextField
                        multiline
                        minRows={3}
                        maxRows={3}
                        size="small" margin="normal" required fullWidth sx={{minWidth: '250px'}}
                        name="comment"
                        value={rowToEdit?.metadata?.comment || ''}
                        label="Note"
                        disabled={loading}
                        onChange={handleFieldChange}
                    >
                    </TextField>
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{mt: 3, float: ' right'}}
                        disabled={loading}
                    >
                        Save
                    </Button>
                </PaperFormWrapper>
            </Modal>
            <Modal open={noteIsOpen} onClose={closeModals}>
                <NoteCard title="Notes" note={openNote} closeHandler={() => setNoteIsOpen(false)}/>
            </Modal>
        </React.Fragment>
    )
}
